// src/pages/SubscriptionPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Typography,
  Table,
  Tag,
  Row,
  Col,
  message,
  Descriptions,
  Card,
  Flex,
} from 'antd';
import {
  CrownOutlined,
  HistoryOutlined,
  ArrowLeftOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Content } from 'antd/es/layout/layout';
import { UserWithSubscriptionsResponse, UserData, Subscription } from '../types/subscription';
import { subscriptionAPI } from '../api/axios';
import Spinner from '../components/Spinner';
import Layout from './Layout';

const { Title } = Typography;

const SubscriptionPage: React.FC = () => {
  const { stripeId, userId } = useParams<{ stripeId: string; userId: string }>();
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [userDetails, setUserDetails] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(false);

  const columns = [
    {
      title: 'PRODUCT',
      dataIndex: 'product',
      key: 'product',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: '14px' }}>{text}</div>
          {record.isCurrentPlan && (
            <Tag color="green" style={{ marginTop: 4 }}>
              Current Plan
            </Tag>
          )}
          {record.features && record.features.length > 0 && (
            <ul className="allSubsListItems">
              {record.features.slice(0, 3).map((feature: string, idx: number) => (
                <li key={idx}>{feature}</li>
              ))}
            </ul>
          )}
        </div>
      ),
    },
    {
      title: 'SUBSCRIPTION ID',
      dataIndex: 'subscriptionId',
      key: 'subscriptionId',
      render: (id: string) => <Tag color="pink">{id}</Tag>,
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (_: string, record: any) => {
        // ----- NEW LOGIC -----
        let tagText = record.status.toUpperCase();
        let color = 'orange';

        if (record.cancelAtperiodEnd) {
          tagText = 'CANCELLING';
          color = 'orange';
        } else if (record.status === 'active') {
          tagText = 'ACTIVE';
          color = 'green';
        } else {
          // ANY OTHER STATUS → RED
          tagText = record.status.toUpperCase();
          color = 'red';
        }

        return <Tag color={color}>{tagText}</Tag>;
      },
    },
    {
      title: 'PERIOD',
      dataIndex: 'period',
      key: 'period',
      render: (text: string, record: any) => (
        <div>
          <div>
            <strong>Start:</strong> {record.startDate}
          </div>
          <div>
            <strong>End:</strong> {record.endDate}
          </div>
        </div>
      ),
    },
    {
      title: 'TOTAL',
      dataIndex: 'total',
      key: 'total',
      render: (text: string) => <strong>{text}</strong>,
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!stripeId || !userId) {
        message.error('Stripe ID and User ID are required');
        navigate('/dashboard');
        return;
      }

      try {
        setLoading(true);
        console.log('🔍 Fetching data for:', { stripeId, userId });

        const data: UserWithSubscriptionsResponse = await subscriptionAPI.getAllSubscriptions(
          stripeId,
          parseInt(userId)
        );

        console.log('📦 API Response:', data);

        if (data.success) {
          if (data.UserDetails?.UserData) {
            setUserDetails(data.UserDetails.UserData);
          }

          if (Array.isArray(data.AllSubscriptionPlans)) {
            setSubscriptions(data.AllSubscriptionPlans);

            if (data.AllSubscriptionPlans.length === 0) {
              message.info('No subscriptions found for this customer');
            } else {
              message.success(`Loaded ${data.AllSubscriptionPlans.length} subscription(s)`);
            }
          }
        } else {
          message.warning('Failed to load data');
          setSubscriptions([]);
        }
      } catch (error: any) {
        console.error('❌ Error fetching data:', error);
        message.error(error.response?.data?.message || 'Failed to load subscription details');
        setSubscriptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [stripeId, userId, navigate]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const currentSubscription = subscriptions.find((sub) => sub.IsCurrentPlan === true);

  const tableData = subscriptions.map((sub, index) => ({
    key: index.toString(),
    product: sub.Plan.PlanName,
    description: sub.Plan.Description,
    // features: sub.Plan.MarketingFeatures || [],
    subscriptionId: sub.SubscriptionId,
    status: sub.Status,
    startDate: formatDate(sub.StartDate),
    endDate: formatDate(sub.EndDate),
    period: `${sub.StartDate} - ${sub.EndDate}`,
    total: `$${sub.Price.Amount.toFixed(2)} / ${sub.Price.BillingPeriod}`,
    isCurrentPlan: sub.IsCurrentPlan,
    cancelAtperiodEnd:sub.CancelAtPeriodEnd,
  }));

  const headerExtra = (
    <Flex align="center" gap={16}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/dashboard')}
        size="small"
        ghost
        className="noStyleBtn"
      >
        Back
      </Button>
    </Flex>
  );

  return (
    <Layout
      pageTitle="View Subscription"
      userName={userDetails?.Name}
      headerExtra={headerExtra}
    >
      <Content className="mainBodylayout">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '100px 20px' }}>
            <Spinner />
          </div>
        ) : (
          <div className="mainBodyInnerContainer">
            <Flex vertical gap={24}>
              {/* User Information Card */}
              <Card>
                <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
                  <Title level={4} style={{ margin: 0 }}>
                    <UserOutlined /> &nbsp;{userDetails?.Name || '—'}
                  </Title>
                  {userDetails?.EmailVerified && <Tag color="green">Email Verified</Tag>}
                </Flex>

                <Row gutter={[24, 16]}>
                  <Col span={24} lg={{ span: 12 }}>
                    <Flex vertical gap={16}>
                      <Flex gap={8}>
                        <label className="contentLabel">Email:</label>
                        <div className="contentRightCol">{userDetails?.Email || '—'}</div>
                      </Flex>
                      <Flex gap={8} style={{ display: 'none' }}>
                        <label className="contentLabel">User ID:</label>
                        <div className="contentRightCol">
                          <Tag color="blue">{userDetails?.UserId || userId}</Tag>
                        </div>
                      </Flex>
                      <Flex gap={8}>
                        <label className="contentLabel">Stripe Customer ID:</label>
                        <div className="contentRightCol">
                          <Tag color="purple">{userDetails?.StripeId || stripeId}</Tag>
                        </div>
                      </Flex>
                    </Flex>
                  </Col>
                  <Col span={24} lg={{ span: 12 }} style={{ display: 'none' }}>
                    <Flex vertical gap={16}>
                      <Flex gap={8}>
                        <label className="contentLabel">Role:</label>
                        <div className="contentRightCol">
                          <Tag color="orange">{userDetails?.RoleName || '—'}</Tag>
                        </div>
                      </Flex>
                      <Flex gap={8}>
                        <label className="contentLabel">Status:</label>
                        <div className="contentRightCol">
                          <Tag color={userDetails?.Status === 'Active' ? 'green' : 'red'}>
                            {userDetails?.Status || '—'}
                          </Tag>
                        </div>
                      </Flex>
                    </Flex>
                  </Col>
                </Row>
              </Card>

              {/* Current Subscription Card */}
              {currentSubscription && (
                <Card>
                  <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
                    <Title level={4} style={{ margin: 0 }}>
                      <CrownOutlined /> &nbsp;Current Subscription
                    </Title>
                    <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
                      {currentSubscription.Plan.PlanName}
                    </Tag>
                  </Flex>

                  <Row gutter={[24, 16]}>
                    <Col span={24} lg={{ span: 12 }}>
                      <Descriptions bordered column={1} size="small" className="customDescriptions">
                        <Descriptions.Item label="Plan Name">
                          {currentSubscription.Plan.PlanName}
                        </Descriptions.Item>
                       <Descriptions.Item label="Status">
                          <Tag
                            color={
                              currentSubscription.CancelAtPeriodEnd
                                ? 'orange'                   // Cancelling → orange
                                : currentSubscription.Status === 'active'
                                ? 'green'                    // Active → green
                                : 'orange'                   // Others → orange
                            }
                          >
                            {currentSubscription.CancelAtPeriodEnd
                              ? 'CANCELLING'
                              : currentSubscription.Status.toUpperCase()}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Start Date">
                          {formatDate(currentSubscription.StartDate)}
                        </Descriptions.Item>
                        <Descriptions.Item label="End Date">
                          {formatDate(currentSubscription.EndDate)}
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                    <Col span={24} lg={{ span: 12 }}>
                      <Descriptions bordered column={1} size="small" className="customDescriptions">
                        <Descriptions.Item label="Billing Amount">
                          <strong style={{ fontSize: '16px', color: '#1890ff' }}>
                            ${currentSubscription.Price.Amount.toFixed(2)}
                          </strong>
                          <span style={{ marginLeft: 8, color: '#8c8c8c' }}>
                            / {currentSubscription.Price.BillingPeriod}
                          </span>
                        </Descriptions.Item>
                        <Descriptions.Item label="Currency">
                          {currentSubscription.Price.Currency.toUpperCase()}
                        </Descriptions.Item>
                        {/* <Descriptions.Item label="Monthly Podcast Limit">
                          {currentSubscription.Plan.MetaData?.monthly_podcast_usage
                            ? `${(parseInt(currentSubscription.Plan.MetaData.monthly_podcast_usage) / 3600).toFixed(1)} hours`
                            : '—'}
                        </Descriptions.Item> */}
                        {/* <Descriptions.Item label="Monthly Storage Limit">
                          {currentSubscription.Plan.MetaData?.monthly_storage_usage
                            ? `${(parseInt(currentSubscription.Plan.MetaData.monthly_storage_usage) / 1000).toFixed(1)} GB`
                            : '—'}
                        </Descriptions.Item> */}
                        <Descriptions.Item label="Recording Limit">
                          {userDetails?.RecordingLimit}{" minutes"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Recorded Duration">
                          {userDetails?.SpentRecording.toFixed(0)}{" minutes"}
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                  </Row>
                </Card>
              )}

              {/* Subscription History */}
              <div>
                <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
                  <Title level={4} style={{ margin: 0 }}>
                    <HistoryOutlined /> &nbsp;All Subscriptions
                  </Title>
                </Flex>
                <Table
                  columns={columns}
                  dataSource={tableData}
                  loading={loading}
                  pagination={false}
                  bordered={false}
                  rowClassName={(record) => (record.isCurrentPlan ? 'current-plan-row' : '')}
                  scroll={{ x: 'max-content' }}
                />
              </div>
            </Flex>
          </div>
        )}
      </Content>
    </Layout>
  );
};

export default SubscriptionPage;